import type { FormErrorEvent } from '@nuxt/ui'
import { nextTick } from 'vue'
import { i18n } from '@/locales'

// Tiny Observer pattern implementation as EventEmitter is EventTarget
// Define event payloads here. Each key is an event name, value is the payload type.
interface IEventMap {
  exampleEventName: string
}

class EventEmitter extends EventTarget {
  listen<K extends keyof IEventMap> (
    eventName: K,
    callback: (data: IEventMap[K]) => void,
    options?: AddEventListenerOptions
  ) {
    const handler = (event: CustomEvent<IEventMap[K]>) => callback(event.detail)
    this.addEventListener(eventName, handler as EventListener, options)

    return {
      remove: () => this.removeEventListener(eventName, handler as EventListener)
    }
  }

  publish<K extends keyof IEventMap> (eventName: K, data: IEventMap[K]): void {
    this.dispatchEvent(new CustomEvent(eventName, { detail: data }))
  }
}

function notificationHandler (
  message: string,
  color: IUITypes['toastColor'] = 'error',
  additionalOptions: IUITypes['toastOptions'] = {}
) {
  const iconByColor: TPartialRecord<IUITypes['toastColor'], string> = {
    error: 'i-lucide-alert-circle',
    success: 'i-lucide-circle-check',
    warning: 'i-lucide-alert-triangle',
    info: 'i-lucide-info'
  }

  const toast = useToast()
  const t = i18n.global.t
  const finalColor = color || 'error'

  const titleByColor: TPartialRecord<IUITypes['toastColor'], string> = {
    error: t('notifications.error'),
    success: t('notifications.success'),
    warning: t('notifications.warning'),
    info: t('notifications.info')
  }

  toast.add({
    color: finalColor,
    title: titleByColor[finalColor] ?? titleByColor.error,
    description: message,
    icon: iconByColor[finalColor] ?? iconByColor.error,
    progress: false,
    orientation: 'horizontal',
    ...additionalOptions
  })
}

const createHashedObject = <
  T extends TIndexedObject,
  Prop extends keyof T,
  ValueArray extends boolean = false
>(
  array: T[] = [],
  prop: Prop = 'id' as Prop,
  isValueArray?: ValueArray,
  shouldRemoveKeyField?: boolean
): Record<T[Prop], ValueArray extends true ? T[] : T> => {
  return array.reduce((acc, current) => {
    const key = current[prop]

    if (shouldRemoveKeyField) {
      delete current[prop]
    }

    if (isValueArray) {
      const existingValue = acc[key] as T[] | undefined
      acc[key] = [...(Array.isArray(existingValue) ? existingValue : []), current] as ValueArray extends true ? T[] : T
    } else {
      acc[key] = current as ValueArray extends true ? T[] : T
    }

    return acc
  }, {} as Record<T[Prop], ValueArray extends true ? T[] : T>)
}

function sortHelpRequests<T extends { status: string; date: string; time: TNullable<string> }> (
  requests: T[],
  statusSortOrder: string[]
): T[] {
  return requests.toSorted((a, b) => {
    const statusDiff = statusSortOrder.indexOf(a.status) - statusSortOrder.indexOf(b.status)

    if (statusDiff !== 0) {
      return statusDiff
    }

    return new Date(`${a.date} ${a.time ?? ''}`).getTime() - new Date(`${b.date} ${b.time ?? ''}`).getTime()
  })
}

function copyToClipboard (data: string) {
  navigator.clipboard.writeText(data)
}

function copyToClipboardWithNotification (data: string, successMessage = i18n.global.t('common.copied')) {
  copyToClipboard(data)
  notificationHandler(successMessage, 'success')
}

function convertBlobToFile (blob: Blob, fileName: string): File {
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now()
  })
}

function asyncTimer (ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function scrollToFormError (event: FormErrorEvent) {
  if (!event.errors.length) {
    return
  }
  await nextTick()
  for (const error of event.errors) {
    if (!error.id) {
      continue
    }
    const el = document.getElementById(error.id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
  }
}

function removeEmptyFromObject<T extends Record<string, unknown>> (obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined)) as T
}

function normalizeTimestampToUtc (value: string): string {
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(value)) {
    return value
  }
  return `${value}Z`
}

function isIOS (): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
}

export const helpers = {
  eventEmitter: new EventEmitter(),
  notificationHandler,
  createHashedObject,
  sortHelpRequests,
  copyToClipboard,
  copyToClipboardWithNotification,
  convertBlobToFile,
  asyncTimer,
  scrollToFormError,
  removeEmptyFromObject,
  isIOS,
  normalizeTimestampToUtc
}
